import { Button, Checkbox } from '@acpaas-ui/react-components';
import _, { get } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import { set } from '../../../../actions/breadcrumbs.actions';
import { fetchReset } from '../../../../actions/fetch.actions';
import FileUpload from '../../../../components/core/FileUpload';
import { INPUT_STATES } from '../../../../components/forms/forms.utils';
import RichTextArea from '../../../../components/forms/RichTextArea';
import SelectField from '../../../../components/forms/SelectField';
import TextField from '../../../../components/forms/TextField';
import { ADMIN_NEW_OFFERING_BREADCRUMBS } from '../../../../constants/breadcrumbs.constants';
import { OFFERING_PERMISSIONS_FIELDS } from '../../../../constants/offering.constants';
import { ADMIN_PATH, ADMIN_TAB_PATHS } from '../../../../constants/paths.constants';
import { DOSSIER_TYPES, DOSSIER_TYPE_OPTIONS } from '../../../../constants/settings.constants';
import formValidators from '../../../../form-validators';
import { useAdminRouteGuard } from '../../../../hooks/admin/useAdminRouteGuard.hook';
import { useForm } from '../../../../hooks/useForm.hook';
import { useTableResource } from '../../../../hooks/useTableResource.hook';
import { getCanSubmitOffering } from '../../../../lib/offering.utils';
import { ADMIN_OFFERINGS_RESOURCE } from '../../../../resources/admin/admin.offerings.resource';
import { OFFERING_FORM_OPTIONS_RESOURCE } from '../../../../resources/offering.form.options.resource';
import { OFFERING_THEMES_RESOURCE } from '../../../../resources/offering.themes.resource';
import adminOfferingsService from '../../../../services/api/admin.offerings.service';

const AdminOfferingNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, input, submitStart, isSubmitting, error, submitError, submitOk } = useForm(
    ADMIN_OFFERINGS_RESOURCE.single.defaultState
  );
  const [validationErrors, setValidationErrors] = useState({});
  const canSubmit = getCanSubmitOffering(data, validationErrors);

  useAdminRouteGuard();

  const { isLoading: isLoadingThemes, data: themesData } = useTableResource(OFFERING_THEMES_RESOURCE, {
    isFetched: true
  });
  const { data: formOptions, isLoading: isLoadingForms } = useTableResource(OFFERING_FORM_OPTIONS_RESOURCE, {
    isFetched: true
  });
  const themesOptions = themesData?.map((t) => ({ value: t.name, label: t.name }));
  const validators = {
    shortDescription: (value) => formValidators.maxLength(value, 255),
    description: (value) => formValidators.maxLength(value, 10000)
  };

  useEffect(() => {
    dispatch(set(ADMIN_NEW_OFFERING_BREADCRUMBS.LOADED()));
  }, [dispatch]);

  const getChangeHandler = (field) => (value) => {
    if (validators[field]) {
      setValidationErrors({ ...validationErrors, [field]: validators[field](value) });
    }
    input(value, field);
  };

  const inputCheckbox = (e) => {
    if (e.target.name === 'visibleUnauthorized' && !!e.target.checked) {
      input(false, 'visibleSchools');
      input(false, 'visiblePartners');
      input(false, 'visibleCity');
    }
    if (e.target.name !== 'visibleUnauthorized' && !!e.target.checked) {
      input(false, 'visibleUnauthorized');
    }
    input(e.target.checked, e.target.name);
  };

  const handleImagesChange = (files) => {
    input(
      _.uniqBy([...data.images, ...Array.from(files)], (f) => f.name),
      'images'
    );
  };

  const handleImageDelete = (file) => {
    input(
      data.images?.filter((f) => f.name !== file.name),
      'images'
    );
  };

  const handleSubmit = () => {
    submitStart();
    adminOfferingsService.add(data, {
      onSuccess: (resp) => {
        submitOk();
        dispatch(fetchReset(ADMIN_OFFERINGS_RESOURCE));
        navigate(generatePath(`${ADMIN_PATH}${ADMIN_TAB_PATHS.OFFERING_DETAIL.path}`, { id: resp.id }));
      },
      onError: submitError
    });
  };

  return (
    <div>
      <h5 className="u-margin-bottom">Nieuw aanbod</h5>
      <div className="row u-margin-bottom-md">
        <div className="col-xs-12">
          <TextField
            label="Titel"
            required
            value={data.title}
            disabled={isSubmitting}
            onChange={getChangeHandler('title')}
            state={INPUT_STATES.DEFAULT}
          />
        </div>
      </div>
      <div className="row u-margin-bottom-md">
        <div className="col-xs-12">
          <TextField
            label="Korte beschrijving zichtbaar op het overzichtscherm"
            value={data.shortDescription}
            disabled={isSubmitting}
            onChange={getChangeHandler('shortDescription')}
            state={INPUT_STATES.DEFAULT}
            hasError={!!validationErrors.shortDescription}
            validationError={validationErrors.shortDescription || ''}
          />
        </div>
      </div>
      <div className="row u-margin-bottom-md">
        <div className="col-xs-12">
          <RichTextArea
            label="Volledige beschrijving"
            value={data.description}
            disabled={isSubmitting}
            onChange={getChangeHandler('description')}
            hasError={!!validationErrors.description}
            validationError={validationErrors.description || ''}
          />
        </div>
      </div>
      <div className="row u-margin-bottom-md">
        <div className="col-xs-12">
          <div className="a-input">
            <label className="a-input__label">Afbeelding</label>
          </div>
          <FileUpload
            multiple
            acceptedFormat="image/*"
            files={data.images}
            onChange={handleImagesChange}
            onDelete={handleImageDelete}
          />
        </div>
      </div>
      <div className="row u-margin-bottom-md">
        <div className="col-xs-12">
          <SelectField
            label="Dossiertype dat aangemaakt moet worden bij aanvraag"
            value={data.dossierType}
            disabled={isSubmitting}
            options={DOSSIER_TYPE_OPTIONS}
            disableSearch
            onChange={getChangeHandler('dossierType')}
            required
          />
        </div>
      </div>
      {data.dossierType && data.dossierType !== DOSSIER_TYPES.NONE && (
        <div className="row u-margin-bottom-md">
          <div className="col-xs-12">
            <SelectField
              label="Aanvraagformulier uit Formscomposer"
              value={data.formSlug}
              options={formOptions}
              isSubmitting={isLoadingForms || isSubmitting}
              disabled={isLoadingForms || isSubmitting}
              onChange={getChangeHandler('formSlug')}
            />
          </div>
        </div>
      )}
      <div className="row u-margin-bottom-md">
        <div className="col-xs-12">
          <SelectField
            label="Label"
            isSubmitting={isLoadingThemes || isSubmitting}
            disabled={isLoadingThemes || isSubmitting}
            options={themesOptions}
            onChange={getChangeHandler('themes')}
            value={data.themes}
            flyoutDirection="up"
            multiple
          />
        </div>
      </div>
      <div className="row u-margin-bottom-lg">
        <div className="col-xs-12">
          <label className="a-input__label">Aanbod is zichtbaar voor:</label>
          <div className="u-margin-top-xs">
            {OFFERING_PERMISSIONS_FIELDS.map((item) => (
              <Checkbox
                id={`checkbox-${item?.field}-id`}
                key={`checkbox-${item?.field}-id`}
                name={item?.field}
                label={item?.label}
                checked={get(data, item?.field) || item?.defaultValue}
                onChange={inputCheckbox}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="row">
        {error && (
          <div className="col-xs-12 u-margin-bottom">
            <p className="u-text-danger">{error}</p>
          </div>
        )}
        <div className="col-xs-12">
          <Button
            iconRight="ai-check-1"
            className="u-margin-right"
            type="success"
            disabled={!canSubmit || isSubmitting}
            onClick={handleSubmit}
          >
            Aanmaken
          </Button>
          <Button negative onClick={() => navigate(-1)}>
            Annuleren
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminOfferingNew;
